import logo from "@/assets/freerange-logo.webp";

export { logo };

export const IMAGES = {
  heroLifestyle: "https://images.unsplash.com/photo-1629360021730-3d258452c425?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  readingTogether: "https://images.pexels.com/photos/8084516/pexels-photo-8084516.jpeg?auto=compress&cs=tinysrgb&w=1200",
  childPencils: "https://images.unsplash.com/photo-1646617747566-b7e784435a48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  childWriting: "https://images.unsplash.com/photo-1553419096-efeafe17bbb7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  notebook: "https://images.unsplash.com/photo-1678822872007-698d622afeb7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  tabletFrame: "https://images.unsplash.com/photo-1612367990403-73ef3e67bc4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  ipad: "https://images.unsplash.com/photo-1527698266440-12104e498b76?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  familyLaptop: "https://images.unsplash.com/photo-1629359652978-a5d383151c4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Apps & Tools", to: "/apps" },
  { label: "Resources", to: "/resources" },
  { label: "Ideas & Guides", to: "/guides" },
  { label: "Contact", to: "/contact" },
];

export const APPS = [
  {
    slug: "spelling",
    name: "Spelling App",
    status: "In Development",
    accent: "purple",
    short: "Personalised spelling practice that fits the learner — not the other way around.",
    summary:
      "A flexible spelling practice app designed for home education, making it easy to create personalised word lists, practise independently and build confidence at each learner's own pace.",
    cta: "Explore Spelling App",
    to: "/apps/spelling",
  },
  {
    slug: "lesson-planner",
    name: "Lesson Planner",
    status: "In Development",
    accent: "turquoise",
    short: "Flexible planning without turning home education into school at home.",
    summary:
      "A practical planning tool for home-educating families, designed to organise lessons, ideas, resources and learning while keeping plans adaptable to real family life.",
    cta: "Explore Lesson Planner",
    to: "/apps/lesson-planner",
  },
];

export const PRINCIPLES = [
  {
    tag: "Flexible by design",
    title: "Use what helps. Ignore what doesn't.",
    body: "Home education looks different for every family, so our tools are designed to flex around the people using them.",
    color: "purple",
  },
  {
    tag: "Made from real experience",
    title: "Built around tools we wanted ourselves.",
    body: "Our ideas begin with genuine needs encountered through home education rather than trying to adapt traditional school systems for home.",
    color: "turquoise",
  },
  {
    tag: "Learner-centred",
    title: "Tools that fit the learner.",
    body: "We aim to make software that adapts around individual learners rather than forcing everyone through the same process.",
    color: "marigold",
  },
  {
    tag: "Practical, not performative",
    title: "Useful structure without unnecessary admin.",
    body: "We focus on features that genuinely help families organise, practise and learn.",
    color: "sunny",
  },
];

export const RESOURCE_CATEGORIES = [
  {
    slug: "planning-organisation",
    title: "Planning & Organisation",
    body: "Templates and tools to help you plan learning in a way that fits your family's rhythm.",
    image: IMAGES.notebook,
    color: "purple",
  },
  {
    slug: "learning-resources",
    title: "Learning Resources",
    body: "Materials and activities to support learning across interests, subjects and ages.",
    image: IMAGES.childPencils,
    color: "turquoise",
  },
  {
    slug: "record-keeping",
    title: "Record Keeping",
    body: "Simple ways to keep useful records without creating paperwork for its own sake.",
    image: IMAGES.childWriting,
    color: "marigold",
  },
];

export const ARTICLES = [
  {
    slug: "flexible-planning-for-home-education",
    title: "Flexible planning for home education",
    excerpt: "How to plan learning in a way that supports your week without locking your family into a rigid timetable.",
    category: "Planning",
    readTime: "5 min read",
    image: IMAGES.notebook,
    featured: true,
    body: [
      "Planning for home education doesn't have to mean recreating a school timetable at the kitchen table. For many families the most useful plans are the ones that leave room to change.",
      "A good starting point is to think about rhythm rather than schedule. What tends to work well in the morning? When does everyone have the most energy for focused work? Building plans around these natural patterns is often far more sustainable than forcing the day into fixed lesson periods.",
      "It also helps to separate the things you'd like to happen this week from the things that must happen. A short list of anchors — a few pieces of practice, a project to keep moving, an outing — gives structure without rigidity.",
      "The aim is not to prescribe how your week should look. It's to give you a frame you can shape around the way your family already learns, and adjust freely as interests and opportunities appear.",
    ],
  },
  {
    slug: "helping-spelling-practice-fit-the-learner",
    title: "Helping spelling practice fit the learner",
    excerpt: "Why word lists built around the individual learner tend to work better than fixed, one-size-fits-all lists.",
    category: "Spelling",
    readTime: "4 min read",
    image: IMAGES.childWriting,
    featured: false,
    body: [
      "Spelling practice is often at its most useful when the words actually matter to the learner — words they're meeting in their reading, their writing and their interests.",
      "Fixed weekly lists can work, but they can also feel arbitrary. When a learner practises words that are relevant to them, practice tends to feel more purposeful and progress feels more real.",
      "Pace matters too. Some learners are ready to move quickly, others benefit from returning to the same words over time. Practice that adapts to the individual, rather than a fixed weekly target, tends to build genuine confidence.",
    ],
  },
  {
    slug: "keeping-useful-home-education-records",
    title: "Keeping useful home-education records",
    excerpt: "A calm, practical approach to records that stays useful without turning into unnecessary admin.",
    category: "Record keeping",
    readTime: "4 min read",
    image: IMAGES.childPencils,
    featured: false,
    body: [
      "Records are most valuable when they help you — when they let you look back, notice progress and remember what you've done together.",
      "You don't need to document everything. A light touch is usually enough: a few notes, some photos, a sense of what's been covered and what's coming next.",
      "The goal is a record you'll actually keep, not paperwork created for the sake of paperwork.",
    ],
  },
  {
    slug: "why-home-education-software-should-not-imitate-school",
    title: "Why home-education software should not imitate school",
    excerpt: "Home education has its own shape. The tools that support it should be built for that shape from the start.",
    category: "Product development",
    readTime: "5 min read",
    image: IMAGES.familyLaptop,
    featured: false,
    body: [
      "A lot of educational software is designed around the systems of school — timetables, classes, terms and fixed progression. That makes sense for schools. It fits home education less naturally.",
      "Home-educating families learn in many different ways: structured plans, projects, interests and everyday life, often all mixed together. Software built for a classroom can quietly push families back towards a school-shaped way of working.",
      "We think tools for home education should start from the needs of home education — flexible, adaptable and shaped around the family, not the institution.",
    ],
  },
];

export const APP_DETAILS = {
  spelling: {
    name: "Spelling App",
    heading: "Spelling practice built around the learner.",
    status: "In Development",
    accent: "purple",
    intro: [
      "The FreeRange.Education Spelling App is being designed to make spelling practice flexible, personalised and easy to manage for home-educating families.",
      "Rather than relying on fixed school-style lists, families will be able to work with words that are relevant to the individual learner and practise at a pace that suits them.",
    ],
    features: [
      { title: "Personalised Word Lists", body: "Create spelling lists around the words each learner actually needs to practise." },
      { title: "Independent Practice", body: "Give learners a simple way to practise without needing constant adult supervision." },
      { title: "Flexible Progress", body: "Work at the learner's own pace rather than following arbitrary weekly targets." },
      { title: "Home-Education Focused", body: "Designed specifically around the needs of home-educating families." },
    ],
    shots: [IMAGES.childWriting, IMAGES.notebook, IMAGES.childPencils],
  },
  "lesson-planner": {
    name: "Lesson Planner",
    heading: "Flexible planning for real home-education life.",
    status: "In Development",
    accent: "turquoise",
    intro: [
      "The FreeRange.Education Lesson Planner is being designed to help families organise learning, resources, ideas and plans without trying to recreate a school timetable at home.",
    ],
    features: [
      { title: "Flexible Planning", body: "Organise learning without forcing every day into rigid lesson periods." },
      { title: "Ideas & Resources", body: "Keep useful links, resources, activities and project ideas together." },
      { title: "Adaptable Learning", body: "Plans can change as interests, opportunities and family life change." },
      { title: "Less Admin", body: "Keep useful records without creating paperwork simply for the sake of paperwork." },
    ],
    shots: [IMAGES.notebook, IMAGES.familyLaptop, IMAGES.readingTogether],
  },
};

export const COMPANY = {
  name: "Sharls Ltd",
  registered: "Registered in England & Wales",
  companyNo: "16965907",
  office: "82A James Carter Road, Mildenhall, IP28 7DE",
  email: "hello@freerange.education",
  positioning: "Tools built for home education — not school software adapted for home.",
};
